import { BACKEND, TRACK_EVENTS } from "core/consts";
import { useInterval } from "core/hooks";
import { getBackendConfig } from "core/model/config";
import { useCallback, useMemo, useState } from "react";
import { useTracking } from "react-tracking";
import {
  INITIAL_RETRY_INTERVAL,
  MINIMUM_DISPLAY_INTERVAL,
  STANDARD_INTERVAL,
} from "./config";

const backoffInterval = (i: number) => (2 ** i - 1) * INITIAL_RETRY_INTERVAL;

function calcWaitTime(pings: number) {
  let waitTime = 0;
  for (let i = pings; i > 0; i--) {
    waitTime += backoffInterval(i);
  }
  return waitTime;
}

export default function useNetworkChecker(): [number, () => void] {
  const [resetPings, setResetPings] = useState<Array<number>>([]);
  const [failedPings, setFailedPings] = useState<number>(0);
  const [isGithubUp, setIsGithubUp] = useState<boolean>(true);
  const { trackEvent } = useTracking();

  const pingUrl = useMemo(() => {
    const backendConfig = getBackendConfig(BACKEND.MATCHING);
    return (
      backendConfig.url ||
      `${backendConfig.protocol}://${backendConfig.host || ""}:${
        backendConfig.port || ""
      }/ping`
    );
  }, []);

  async function failure() {
    try {
      await fetch("https://api.github.com/");
    } catch (_error) {
      setIsGithubUp(false);
    }
    setFailedPings(failedPings + 1);
  }

  const success = useCallback(() => {
    if (failedPings > 1 || (failedPings == 1 && resetPings.length > 0)) {
      const cumulativePings = resetPings.reduce(
        (accumulatedPings, ping) => accumulatedPings + ping,
        0,
      );
      const cumulativeWaitTime = resetPings.reduce(
        (accumulatedTime, ping) => accumulatedTime + calcWaitTime(ping),
        0,
      );
      const did_reset = cumulativeWaitTime > 0;
      const reseted_wait_times = did_reset
        ? resetPings.map((ping) => calcWaitTime(ping))
        : undefined;
      const totalWaitTime = cumulativeWaitTime + calcWaitTime(failedPings);
      trackEvent({
        did_ping_github: isGithubUp,
        did_reset,
        did_show_banner: totalWaitTime > MINIMUM_DISPLAY_INTERVAL,
        failed_pings: failedPings + cumulativePings,
        name: TRACK_EVENTS.NETWORK_CHECKER,
        reseted_wait_times,
        wait_time: totalWaitTime,
      });
    }
    // reset state
    setFailedPings(0);
    setResetPings([]);
    setIsGithubUp(true);
  }, [resetPings, failedPings, isGithubUp]);

  const reset = useCallback(() => {
    const nextResetPings = resetPings.slice();
    nextResetPings.push(failedPings - 1);
    setResetPings(nextResetPings);
    setFailedPings(1);
  }, [resetPings, failedPings]);

  useInterval(
    () => {
      fetch(pingUrl)
        .then(async (response) => {
          const text = await response.text();
          if (text !== "pong") failure();
          else success();
        })
        .catch(() => {
          failure();
        });
    },
    failedPings > 0 ? backoffInterval(failedPings) : STANDARD_INTERVAL,
  );

  return [failedPings > 0 ? backoffInterval(failedPings) : 0, reset];
}
